import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  Crown,
  Shield,
  RefreshCw,
  Check,
  X,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  AlertTriangle,
  BarChart3,
  CreditCard,
  Users,
  FileText,
  Zap,
  Settings,
  History,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// Access mode styles
const ACCESS_MODE_STYLES = {
  hidden: { bg: 'bg-slate-700', text: 'text-slate-300', icon: EyeOff, label: 'Hidden' },
  locked: { bg: 'bg-amber-900/50', text: 'text-amber-400', icon: Lock, label: 'Locked' },
  read_only: { bg: 'bg-blue-900/50', text: 'text-blue-400', icon: Eye, label: 'Read Only' },
  full_access: { bg: 'bg-emerald-900/50', text: 'text-emerald-400', icon: Unlock, label: 'Full Access' },
};

// Membership status styles
const STATUS_STYLES = {
  active: { bg: 'bg-emerald-100', text: 'text-emerald-700', dark: 'bg-emerald-900/50 text-emerald-400' },
  trial: { bg: 'bg-blue-100', text: 'text-blue-700', dark: 'bg-blue-900/50 text-blue-400' },
  past_due: { bg: 'bg-amber-100', text: 'text-amber-700', dark: 'bg-amber-900/50 text-amber-400' },
  suspended: { bg: 'bg-red-100', text: 'text-red-700', dark: 'bg-red-900/50 text-red-400' },
  cancelled: { bg: 'bg-slate-100', text: 'text-slate-600', dark: 'bg-slate-700 text-slate-400' },
};

/**
 * UsageCounterBar - Display a usage counter with progress bar
 */
const UsageCounterBar = ({ counter, onReset }) => {
  const current = counter.current_value || 0;
  const limit = counter.limit_value;
  const percentage = limit ? Math.min(100, (current / limit) * 100) : 0;
  
  const getBarColor = () => {
    if (percentage >= 95) return 'bg-red-500';
    if (percentage >= 80) return 'bg-amber-500';
    return 'bg-emerald-500';
  };
  
  const getStatusColor = () => {
    if (percentage >= 95) return 'text-red-400';
    if (percentage >= 80) return 'text-amber-400';
    return 'text-emerald-400';
  };
  
  return (
    <div className="bg-stone-800 rounded-lg p-4" data-testid={`counter-${counter.counter_key}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium text-white capitalize">
          {counter.counter_key.replace(/_/g, ' ')}
        </span>
        <span className={`text-sm font-medium ${getStatusColor()}`}>
          {current} / {limit ?? '∞'}
        </span>
      </div>
      
      {limit && (
        <div className="w-full h-2 bg-stone-700 rounded-full overflow-hidden mb-2">
          <div 
            className={`h-full ${getBarColor()} transition-all`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      )}
      
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500">
          {counter.period !== 'none' && `Resets ${counter.period}`}
        </span>
        {onReset && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onReset(counter.counter_key)}
            className="text-xs text-slate-400 hover:text-white h-6 px-2"
          >
            Reset
          </Button>
        )}
      </div>
    </div>
  );
};

/**
 * SectionAccessControl - Control for a single section's access mode
 */
const SectionAccessControl = ({ sectionKey, currentMode, onChange }) => {
  const style = ACCESS_MODE_STYLES[currentMode] || ACCESS_MODE_STYLES.hidden;
  const Icon = style.icon;
  
  return (
    <div className="flex items-center justify-between py-2 px-3 bg-stone-800 rounded-lg">
      <div className="flex items-center gap-3">
        <div className={`p-1.5 rounded ${style.bg}`}>
          <Icon size={14} className={style.text} />
        </div>
        <span className="text-sm text-white capitalize">{sectionKey.replace(/_/g, ' ')}</span>
      </div>
      <Select value={currentMode} onValueChange={(val) => onChange(sectionKey, val)}>
        <SelectTrigger className="w-32 h-8 bg-stone-700 border-slate-600 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="hidden">Hidden</SelectItem>
          <SelectItem value="locked">Locked</SelectItem>
          <SelectItem value="read_only">Read Only</SelectItem>
          <SelectItem value="full_access">Full Access</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};

/**
 * AgencyMembershipTab - Per-agency membership management
 */
const AgencyMembershipTab = ({ agencyId }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [membership, setMembership] = useState(null);
  const [plan, setPlan] = useState(null);
  const [counters, setCounters] = useState([]);
  const [sectionAccess, setSectionAccess] = useState({});
  const [plans, setPlans] = useState([]);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [membershipRes, plansRes] = await Promise.all([
        axios.get(`${API}/admin/membership/memberships/${agencyId}`, { headers }),
        axios.get(`${API}/admin/membership/plans`, { headers }),
      ]);
      
      setMembership(membershipRes.data.membership);
      setPlan(membershipRes.data.plan);
      setCounters(membershipRes.data.usage_counters || []);
      setPlans(plansRes.data.plans || []);
      
      // Fetch section access
      try {
        const accessRes = await axios.get(`${API}/admin/membership/section-access/${agencyId}`, { headers });
        setSectionAccess(accessRes.data.section_access || {});
      } catch (e) {
        setSectionAccess({});
      }
    } catch (err) {
      console.error('Failed to fetch membership:', err);
      // Agency might not have a membership yet
      setMembership(null);
      try {
        const plansRes = await axios.get(`${API}/admin/membership/plans`, { headers });
        setPlans(plansRes.data.plans || []);
      } catch (e) {}
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && agencyId) fetchData();
  }, [token, agencyId]);

  const handleCreateMembership = async (planId) => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/membership/memberships`, {
        agency_id: agencyId,
        plan_id: planId,
      }, { headers });
      toast.success('Membership created');
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to create membership');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePlan = async (planId) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/membership/memberships/${agencyId}`, {
        plan_id: planId,
      }, { headers });
      toast.success('Plan changed');
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to change plan');
    } finally {
      setSaving(false);
    }
  };

  const handleSuspend = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/membership/memberships/${agencyId}/suspend`, {
        reason: 'Admin suspension',
      }, { headers });
      toast.success('Membership suspended');
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to suspend');
    } finally {
      setSaving(false);
    }
  };

  const handleReactivate = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/membership/memberships/${agencyId}/reactivate`, {}, { headers });
      toast.success('Membership reactivated');
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to reactivate');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSectionAccess = async (sectionKey, accessMode) => {
    try {
      await axios.put(`${API}/admin/membership/section-access/${agencyId}`, {
        section_key: sectionKey,
        access_mode: accessMode,
      }, { headers });
      setSectionAccess(prev => ({ ...prev, [sectionKey]: accessMode }));
      toast.success(`${sectionKey} set to ${accessMode}`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to update section access');
    }
  };

  const handleResetCounter = async (counterKey) => {
    try {
      await axios.post(`${API}/admin/membership/usage/${agencyId}/${counterKey}/reset`, {}, { headers });
      toast.success(`${counterKey} reset`);
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to reset counter');
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 bg-stone-800" />
        <Skeleton className="h-64 bg-stone-800" />
      </div>
    );
  }

  // No membership - show creation UI
  if (!membership) {
    return (
      <div className="bg-stone-800 border border-slate-700 rounded-xl p-8 text-center">
        <Crown size={48} className="mx-auto text-slate-600 mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">No Membership</h3>
        <p className="text-slate-400 mb-6">This agency does not have a membership assigned.</p>
        
        <div className="max-w-md mx-auto">
          <label className="block text-sm text-slate-400 mb-2 text-left">Assign a Plan</label>
          <Select onValueChange={handleCreateMembership} disabled={saving}>
            <SelectTrigger className="w-full bg-stone-700 border-slate-600">
              <SelectValue placeholder="Select a plan..." />
            </SelectTrigger>
            <SelectContent>
              {plans.map(p => (
                <SelectItem key={p.plan_id} value={p.plan_id}>
                  {p.name} - ${p.monthly_price}/mo
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    );
  }

  const statusStyle = STATUS_STYLES[membership.status] || STATUS_STYLES.active;

  return (
    <div className="space-y-6">
      {/* Membership Overview Card */}
      <div className="bg-gradient-to-r from-amber-600 to-amber-700 rounded-xl p-6 text-white">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Crown size={24} />
              <h3 className="text-xl font-bold">{plan?.name || membership.plan_id}</h3>
              <Badge className={statusStyle.dark}>{membership.status}</Badge>
            </div>
            <p className="text-amber-100">{plan?.description}</p>
            <p className="text-xs text-amber-200 mt-2 font-mono">
              Membership ID: {membership.membership_id}
            </p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold">${plan?.monthly_price || 0}</p>
            <p className="text-amber-100 text-sm">/month</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Plan & Status */}
        <div className="bg-stone-800 border border-slate-700 rounded-xl p-5">
          <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
            <Settings size={18} className="text-amber-500" />
            Plan & Status
          </h4>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">Current Plan</label>
              <Select 
                value={membership.plan_id} 
                onValueChange={handleChangePlan}
                disabled={saving}
              >
                <SelectTrigger className="w-full bg-stone-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {plans.map(p => (
                    <SelectItem key={p.plan_id} value={p.plan_id}>
                      {p.name} - ${p.monthly_price}/mo
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-2">Billing Cycle</label>
              <div className="px-3 py-2 bg-stone-700 rounded-lg text-white capitalize">
                {membership.billing_cycle}
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-2">Status Actions</label>
              <div className="flex gap-2">
                {membership.status === 'active' || membership.status === 'trial' ? (
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    onClick={handleSuspend}
                    disabled={saving}
                    className="flex-1"
                  >
                    <X size={14} className="mr-2" />
                    Suspend
                  </Button>
                ) : (
                  <Button 
                    size="sm" 
                    onClick={handleReactivate}
                    disabled={saving}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Check size={14} className="mr-2" />
                    Reactivate
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Usage Counters */}
        <div className="bg-stone-800 border border-slate-700 rounded-xl p-5">
          <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
            <BarChart3 size={18} className="text-emerald-500" />
            Usage Counters
          </h4>
          
          <div className="space-y-3">
            {counters.length > 0 ? (
              counters.map(counter => (
                <UsageCounterBar 
                  key={counter.counter_key} 
                  counter={counter}
                  onReset={handleResetCounter}
                />
              ))
            ) : (
              <p className="text-slate-500 text-sm text-center py-4">
                No usage counters configured
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Section Access Control */}
      <div className="bg-stone-800 border border-slate-700 rounded-xl p-5">
        <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
          <Lock size={18} className="text-blue-500" />
          Section Access Control
        </h4>
        <p className="text-sm text-slate-400 mb-4">
          Override section access modes for this agency. Changes take effect immediately.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {Object.entries(sectionAccess).length > 0 ? (
            Object.entries(sectionAccess).map(([key, mode]) => (
              <SectionAccessControl
                key={key}
                sectionKey={key}
                currentMode={mode}
                onChange={handleUpdateSectionAccess}
              />
            ))
          ) : (
            <div className="col-span-2 text-center py-6">
              <Lock size={32} className="mx-auto text-slate-600 mb-2" />
              <p className="text-slate-500 text-sm">
                Section access uses plan defaults. Add overrides above.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Payment Processor Access */}
      <div className="bg-stone-800 border border-slate-700 rounded-xl p-5">
        <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
          <CreditCard size={18} className="text-purple-500" />
          Payment Processor Access
        </h4>
        
        <div className="flex items-center justify-between p-4 bg-stone-900 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-900/50 rounded-lg flex items-center justify-center">
              <CreditCard className="text-purple-400" size={20} />
            </div>
            <div>
              <p className="font-medium text-white">Stripe</p>
              <p className="text-xs text-slate-400">Payment processing</p>
            </div>
          </div>
          <Badge className={
            membership.allowed_payment_processors?.includes('stripe')
              ? 'bg-emerald-900/50 text-emerald-400'
              : 'bg-red-900/50 text-red-400'
          }>
            {membership.allowed_payment_processors?.includes('stripe') ? 'Enabled' : 'Disabled'}
          </Badge>
        </div>
      </div>
    </div>
  );
};

export default AgencyMembershipTab;
